<template>
  <div>
    <v-expand-transition v-if="shouldDisplayTray">
      <AppFileManager @cancel="onTrayCloseClick" @minimize="watermarkAlertDismissed = true">
        <template #header>
          <span v-t="{
            path: 'common.app-file-upload.AppFileUpload.title',
            plural: successFilders,
            args: { successCount: successFilders, totalCount: uploadQueue.length },
          }" />
        </template>
        <template #content>
          <app-alert-in-page
            v-if="showWatermarkAlert"
            class="AppFileUpload__watermarkAlert"
            variant="info"
            :hasLeftBorder="false"
          >
            {{ $t('project.documents.Documents.watermarkInfos') }}
          </app-alert-in-page>
          <div class="AppFileUpload__fildersContainer text-text-secondary">
            <template v-for="filder in sortedFilesToUpload" :key="filder.key">
              <component :is="filder.type === 'file' ? 'AppFileUploadFile' : 'AppFileUploadFolder'"
                         :file="filder"
                         :folder="filder"
              />
            </template>
          </div>
        </template>
      </AppFileManager>
    </v-expand-transition>
    <RoomStorageExceededDialog
      v-if="roomStorageExceededDialogIsOpen"
      @close="onRoomStorageExceededDialogClose"
    />
    <CancelAllFilesDialog v-if="cancelAllFilesDialogIsOpen"
                          @close="cancelAllFilesDialogIsOpen = false"
    />
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'

import RoomStorageExceededDialog from '@/common/app-file-upload/RoomStorageExceededDialog.vue'
import AppFileManager from '@/common/AppFileManager.vue'
import { ROOM_DOCUMENTS_ROUTE_NAME } from '@/router/routerData'
import { AppUploadHandlerMutationTypes } from '@/store/modules/app-upload-handler/mutation_types'
import { SET_ROOM_STORAGE_EXCEEDED_DIALOG_IS_OPEN } from '@/store/modules/room/mutation_types'

import AppFileUploadFile from './AppFileUploadFile.vue'
import AppFileUploadFolder from './AppFileUploadFolder.vue'
import CancelAllFilesDialog from './CancelAllFilesDialog.vue'
import { uploadStatus } from './uploadStatus'
import { uploadTypes } from './uploadTypes'

export default {
  name: 'AppFileUpload',
  components: {
    AppFileManager,
    AppFileUploadFile,
    AppFileUploadFolder,
    CancelAllFilesDialog,
    RoomStorageExceededDialog,
  },
  data () {
    return {
      cancelAllFilesDialogIsOpen: false,
      // Once the tray has been minimized, the watermark alert stays hidden when it is expanded again
      watermarkAlertDismissed: false,
    }
  },
  computed: {
    ...mapState('appUploadHandler', ['filesToUpload', 'uploadQueue', 'trayManuallyClosed']),
    ...mapState('room', ['roomStorageExceededDialogIsOpen', 'currentRoom']),
    successFilders () {
      return this.uploadQueue.filter(f => f.status === uploadStatus.SUCCESS).length
    },
    showWatermarkAlert () {
      return Boolean(this.currentRoom?.isDataroom && this.currentRoom?.watermarkEnabled) && !this.watermarkAlertDismissed
    },
    // Document uploads are only shown while on the Documents page (with their history restored on return);
    // other upload contexts (e.g. checklist attachments) keep the previous, route-independent behavior
    isDocumentsRoute () {
      return this.$route.name === ROOM_DOCUMENTS_ROUTE_NAME
    },
    hasNonDocumentUploads () {
      return this.uploadQueue.some(file => file.uploadType !== uploadTypes.DOCUMENT)
    },
    hasActiveUploads () {
      return this.uploadQueue.some(file => file.status === uploadStatus.PENDING || file.status === uploadStatus.IN_PROGRESS)
    },
    shouldDisplayTray () {
      return Boolean(this.filesToUpload.length) &&
        !this.trayManuallyClosed &&
        (this.hasNonDocumentUploads || this.isDocumentsRoute)
    },
    // Display the most recent uploads at the top of the tray
    sortedFilesToUpload () {
      return [...this.filesToUpload].reverse()
    },
  },
  watch: {
    // The tray is remounted expanded the next time it is displayed, so the alert is shown again
    shouldDisplayTray (isDisplayed) {
      if (!isDisplayed) {
        this.watermarkAlertDismissed = false
      }
    },
    // Leaving a room goes through Project.vue being torn down (killRoom nulls currentRoom)
    // before the next room's data loads, so the actual mnemo change happens across two separate
    // transitions with a null in between - reset on either one, not only when both sides are set.
    'currentRoom.mnemo' (newMnemo, oldMnemo) {
      if (newMnemo !== oldMnemo) {
        this.RESET()
      }
    },
  },
  methods: {
    ...mapMutations('room', [SET_ROOM_STORAGE_EXCEEDED_DIALOG_IS_OPEN]),
    ...mapMutations('appUploadHandler', {
      SET_TRAY_MANUALLY_CLOSED: AppUploadHandlerMutationTypes.SET_TRAY_MANUALLY_CLOSED,
      RESET: AppUploadHandlerMutationTypes.RESET,
    }),
    onRoomStorageExceededDialogClose () {
      this.SET_ROOM_STORAGE_EXCEEDED_DIALOG_IS_OPEN(false)
    },
    onTrayCloseClick () {
      if (this.hasActiveUploads) {
        this.cancelAllFilesDialogIsOpen = true
        return
      }
      this.SET_TRAY_MANUALLY_CLOSED(true)
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.AppFileUpload {
  &__watermarkAlert {
    padding: 16px !important;
    border: get-border('border-info');
    @include border-radius('border-radius-3');
    border-left-color: rgb(var(--v-theme-border-info)) !important;
    margin-bottom: 12px;
  }

  &__fildersContainer {
    max-height: 380px;
    overflow-y: auto;
    overflow-x: hidden;
  }
}
</style>
